<div class="p-5 lg:p-8">

  <!-- ============================================================ -->
  <!-- Header                                                       -->
  <!-- ============================================================ -->
  <div class="max-w-7xl mx-auto mb-6">
    <div class="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
      <span>Dashboard</span>
      <span class="text-slate-300">/</span>
      <span class="text-slate-700">Reportes</span>
    </div>
    <h1 class="text-2xl font-semibold text-slate-900 tracking-tight">Centro de Reportes</h1>
    <p class="text-sm text-slate-500 mt-1 tabular-nums">
      {{ reports.length }} reportes · Visualizá, exportá y analizá tu información financiera y académica.
    </p>
  </div>

  <!-- ============================================================ -->
  <!-- Search hero                                                  -->
  <!-- ============================================================ -->
  <div class="max-w-7xl mx-auto mb-6">
    <div class="search-hero">
      <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
      </svg>
      <input type="text" [(ngModel)]="searchTerm"
        placeholder="Buscar reporte por nombre, descripción o categoría…">
      <button *ngIf="searchTerm" (click)="clearSearch()" class="clear-btn" title="Limpiar búsqueda">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- Más usados (solo cuando no hay búsqueda)                     -->
  <!-- ============================================================ -->
  <div *ngIf="!searchTerm" class="max-w-7xl mx-auto mb-8">
    <div class="flex items-center gap-2 mb-4">
      <svg class="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
        <path d="M11.49 3.17c-.38-.94-1.6-.94-1.98 0L7.62 8.79l-6.05.42c-1.02.07-1.43 1.31-.66 1.95l4.7 3.89-1.5 5.85c-.25.98.82 1.76 1.7 1.21l5.17-3.21 5.17 3.21c.88.55 1.95-.23 1.7-1.21l-1.5-5.85 4.7-3.89c.77-.64.36-1.88-.66-1.95l-6.05-.42-1.85-5.62z"/>
      </svg>
      <h2 class="text-xs font-semibold text-slate-600 uppercase tracking-wider">Más usados</h2>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <a *ngFor="let report of popularReports"
        [routerLink]="report.route"
        class="report-card report-card--featured">
          <span *ngIf="report.isNew" class="report-badge-new">Nuevo</span>
        <span class="report-icon"
          [ngClass]="'tone-' + (categories | slice:0:1)[0].tone">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
            <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="report.iconPath" />
          </svg>
        </span>
        <h3 class="report-name">{{ report.name }}</h3>
        <p class="report-description">{{ report.description }}</p>
        <div class="report-footer">
          <span class="report-tag">Destacado</span>
          <span class="report-arrow">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </span>
        </div>
      </a>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- No results                                                   -->
  <!-- ============================================================ -->
  <div *ngIf="filteredReports.length === 0" class="max-w-7xl mx-auto">
    <div class="no-results">
      <div class="no-results-icon">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
      </div>
      <p class="text-sm font-medium text-slate-700">No encontramos reportes para "<span class="text-slate-900">{{ searchTerm }}</span>"</p>
      <p class="text-xs text-slate-500 mt-1">Probá con otro término o limpiá la búsqueda.</p>
      <button (click)="clearSearch()" class="btn btn-secondary btn-sm mt-3 inline-flex">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        Limpiar búsqueda
      </button>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- Categorías                                                   -->
  <!-- ============================================================ -->
  <div class="max-w-7xl mx-auto space-y-10">
    <section *ngFor="let category of visibleCategories">

      <!-- Category header -->
      <div class="category-header">
        <span class="category-icon" [ngClass]="'tone-' + category.tone">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
            <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="category.iconPath" />
          </svg>
        </span>
        <div class="category-meta">
          <div class="category-name">
            {{ category.name }}
            <span class="category-count">{{ reportsByCategory(category.key).length }}</span>
          </div>
          <p class="category-description">{{ category.description }}</p>
        </div>
      </div>

      <!-- Reports grid -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        <a *ngFor="let report of reportsByCategory(category.key)"
          [routerLink]="report.route" class="report-card">
          <span *ngIf="report.isNew" class="report-badge-new">Nuevo</span>
          <span class="report-icon" [ngClass]="'tone-' + category.tone">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
              <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="report.iconPath" />
            </svg>
          </span>
          <h3 class="report-name">{{ report.name }}</h3>
          <p class="report-description">{{ report.description }}</p>
          <div class="report-footer">
            <span class="report-tag">{{ category.name }}</span>
            <span class="report-arrow">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </div>
        </a>
      </div>
    </section>
  </div>

</div>
